import { withErrorBoundary } from 'app/components/error-boundary';
import { useModalCardLayout } from 'app/screens/intro/use-modal-card-layout';
import { SubscriptionSuccess } from 'app/screens/intro/present-community-screen-body';
import { navigateToMyPlan } from 'app/screens/root-stack-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import { useUISize } from 'app/utils/web-helpers';
import { ActivityIndicator, View } from 'react-native';
import { restoreGuestReturnLocation } from 'app/lib/guest-return-location';
import { recordError } from 'app/lib/monitoring';
import { useUpsertOnePreference } from 'app/network/preferences';
import { TrialModalType } from 'app/types/preferences';
import { isFreeTrialPlanCode, useMyActivePlans } from 'app/network/plan';
import { useEffect } from 'react';
import { useSetAtom } from 'jotai';
import { presentB2CPaidModalAtom } from 'app/atoms/ui';
import { useNavigation } from 'app/hooks/navigation';
import { navigateToLandingPage } from 'app/screens/billboard/navigator-helper';

const theme = createDSTheme();

const PaymentSuccessScreenComponent: NavigationScreen<'payment-success'> = () => {
  const navigation = useNavigation<'payment-success'>();
  const setPresentB2CPaidModal = useSetAtom(presentB2CPaidModalAtom);
  const { isLargeUI } = useUISize();
  const { cardWidth, cardHeight, maxWidth, onCardLayout } = useModalCardLayout();
  const { data: plans, isPending: isPendingPlans } = useMyActivePlans();
  const paidPlan = plans?.[0] && !isFreeTrialPlanCode(plans[0].code.code) ? plans[0] : undefined;
  const { mutateAsync: updateTrialModalPreference, isPending: isUpdatingPreference } =
    useUpsertOnePreference('trialModal');

  useEffect(() => {
    // Consume pending success notifications, including when opened directly after checkout.
    setPresentB2CPaidModal(false);
  }, [setPresentB2CPaidModal]);

  useEffect(() => {
    if (!isPendingPlans && !paidPlan) navigateToMyPlan();
  }, [isPendingPlans, paidPlan]);

  const onContinue = async () => {
    try {
      await updateTrialModalPreference(TrialModalType.never);
    } catch (error) {
      recordError(error);
    }
    if (restoreGuestReturnLocation()) return;
    navigateToLandingPage(navigation);
  };

  if (isPendingPlans || !paidPlan) {
    return (
      <View style={{ alignItems: 'center', flex: 1, justifyContent: 'center' }}>
        <ActivityIndicator color={theme.colors['surface.brand.default']} size="large" />
      </View>
    );
  }

  const content = <SubscriptionSuccess isContinuing={isUpdatingPreference} onContinue={() => void onContinue()} />;

  if (!isLargeUI) return <View style={{ flex: 1 }}>{content}</View>;

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors['surface.overlay'] }}>
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: theme.spacing['spacing.general.x4'],
        }}
      >
        <View
          onLayout={onCardLayout}
          style={{
            width: cardWidth,
            height: cardHeight,
            maxWidth,
            borderRadius: theme.borderRadii['radius.xl'],
            overflow: 'hidden',
            backgroundColor: theme.colors['surface.tertiary'],
          }}
        >
          {content}
        </View>
      </View>
    </View>
  );
};

export const PaymentSuccessScreen = withErrorBoundary(PaymentSuccessScreenComponent);
